import { convertCommodityToHandlingUnit } from "~/features/quote/utils/commodity-to-handling-unit.ts";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";
import type { UnitType } from "~/shared/schemas/unit-type-schema.ts";
import type { HandlingUnit } from "~/shared/types/quote-details-types";
import { getEstClass } from "~/shared/utils/commodity-utils.ts";
import { idGenerator } from "~/shared/utils/id-generator.ts";

const EMPTY_CLASS = { id: 0, name: "", code: "" };
const EMPTY_UNIT = { id: 0, name: "", code: "" };

/**
 * Builds a shipment editor handling unit from a saved commodity. Reuses the
 * quote flow's conversion, then adjusts it for the shipment editor:
 * - gives each unit a fresh id so picking the same commodity twice doesn't
 *   collide;
 * - resolves the unit against reference data, because the shipment persists
 *   the unit code (e.g. "PLT") and the saved commodity only carries its name.
 *   An unresolved unit is left blank so required-unit validation makes the
 *   user pick one, instead of persisting the name as if it were a code;
 * - blanks a missing class so required-class validation still applies;
 * - derives the estimated class from the density.
 */
export const mapSavedCommodityToHandlingUnit = (
	commodity: Commodity,
	unitTypes: UnitType[] = [],
): HandlingUnit => {
	const handlingUnit = convertCommodityToHandlingUnit(commodity, "LB/IN");
	const unitType = unitTypes.find((type) => type.id === commodity.unitTypeId);

	return {
		...handlingUnit,
		id: idGenerator(),
		unit: unitType ?? EMPTY_UNIT,
		class: commodity.freightClass ? handlingUnit.class : EMPTY_CLASS,
		estClass: getEstClass(handlingUnit.density),
	};
};
